// Employee pay helpers — API docs §11.
//
// Salary is revision history, not a single field: §11.1 keeps every revision so
// a past month stays priced on whatever was in force at that month's end.
// §11.2 answers with `{ employee, current, upcoming, history }`, where
// `current` is null until a salary has ever been set.

/**
 * Current monthly pay, given either a §11.2 salary response or a record that
 * embeds its salary (a §11.3 row, or an employee carrying a work profile).
 * Returns null when no salary has been set, which is a real state worth
 * distinguishing from zero.
 */
export function readCurrentSalary(source) {
  if (!source) return null;
  const candidates = [
    // §11.2 — { current: { monthlyAmount, effectiveFrom } }
    source.current?.monthlyAmount,
    // §11.3 row — { salarySet, monthlyAmount, effectiveFrom }
    source.salarySet ? source.monthlyAmount : undefined,
    // §12 work profile embedded on an employee
    source.salary?.monthlyAmount,
    // A payroll sheet row (§11.4)
    source.monthlyAmount,
  ];
  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

/** The date the current figure took effect, when the source carries one. */
export function readSalaryEffectiveFrom(source) {
  if (!source) return null;
  return source.current?.effectiveFrom ?? source.effectiveFrom ?? source.salary?.effectiveFrom ?? null;
}

/** A scheduled future revision (§11.2's `upcoming`), or null. */
export function readUpcomingSalary(source) {
  return source?.upcoming ?? null;
}

/** Formats an amount as Indian rupees, e.g. 25000 -> "₹25,000". */
export function formatMoney(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `₹${amount}`;
  }
}
